import { ItemsResponse, SingleResponse } from "@tastyworks/tastyworks-api"
import { RestError, type RestContext } from "./errors/rest-error"
import { rethrowIfHandledGlobally } from "./internal-helpers"
import { Err, Ok } from "./result"

/**
 * Wraps an async callback in a try/catch and returns a result object instead of
 * throwing, so errors can be handled without a surrounding try/catch block.
 *
 * @param callback The async function to invoke.
 * @param args Arguments forwarded to `callback`.
 * @returns A promise resolving to `Ok<T>` (with `value`) on success, or `Err`
 * (with `error`) if the callback throws. Narrow on the `ok` discriminant.
 * @throws `InvalidSessionError` and `TwoFactorAuthError` only. Both are handled
 * above the call site, so they must not be swallowed here.
 *
 * @example
 * const result = await tc(fetchCustomer, customerId)
 * if (result.ok) {
 *   use(result.value)
 * } else {
 *   reportAndRecover(result.error)
 * }
 */
export async function tc<T, A extends unknown[]>(
  callback: (...args: A) => Promise<T>,
  ...args: A
): Promise<Ok<T> | Err> {
  try {
    const result = await callback(...args)
    return new Ok(result)
  } catch (e) {
    rethrowIfHandledGlobally(e)
    return new Err(e)
  }
}

type SettledTuple<T extends Array<() => unknown>> = {
  [K in keyof T]: Ok<Awaited<ReturnType<T[K]>>> | Err
}

/**
 * Behaves exactly like `Promise.allSettled` over a list of thunks, except each
 * outcome is wrapped in `Ok`/`Err` rather than `PromiseSettledResult`, so the
 * results narrow and unwrap the same way as {@link tc}'s.
 *
 * @param callbacks The functions to invoke. Each is called with no arguments.
 * @returns A promise resolving to a tuple in the same order as `callbacks`,
 * where each entry is `Ok` (with `value`) or `Err` (with `error`). Narrow on
 * the `ok` discriminant.
 * @throws `InvalidSessionError` and `TwoFactorAuthError` only. Both are handled
 * above the call site, so they must not be swallowed here. Every callback has
 * already settled by then, so no sibling request is left in flight.
 *
 * @example
 * const [customer, agreements] = await tcAllSettled([
 *   () => fetchCustomer(customerId),
 *   () => fetchAgreements(regulatoryDomain),
 * ])
 * if (customer.ok) {
 *   use(customer.value)
 * } else {
 *   reportAndRecover(customer.error)
 * }
 */
export async function tcAllSettled<T extends Array<() => unknown>>(
  callbacks: [...T]
): Promise<SettledTuple<T>> {
  const settledList = await Promise.allSettled(callbacks.map((cb) => cb()))
  // Promise.allSettled preserves input order and length; .map() erases that
  // from the type but not at runtime, so the assertion is sound
  return settledList.map((settled) => {
    if (settled.status === "fulfilled") return new Ok(settled.value)
    else {
      rethrowIfHandledGlobally(settled.reason)
      return new Err(settled.reason)
    }
  }) as SettledTuple<T>
}

/**
 * Synchronous counterpart to {@link tc}. Wraps a synchronous callback in a
 * try/catch and returns a result object instead of throwing.
 *
 * @param callback The function to invoke.
 * @param args Arguments forwarded to `callback`.
 * @returns `Ok<T>` (with `value`) on success, or `Err` (with `error`) if the
 * callback throws. Narrow on the `ok` discriminant.
 *
 * @example
 * const result = tcSync(JSON.parse, raw)
 * if (result.ok) {
 *   use(result.value)
 * } else {
 *   reportAndRecover(result.error)
 * }
 */
export function tcSync<T, A extends unknown[]>(
  callback: (...args: A) => T,
  ...args: A
): Ok<T> | Err {
  try {
    const result = callback(...args)
    return new Ok(result)
  } catch (e) {
    return new Err(e)
  }
}

/**
 * The way to call a REST endpoint. Behaves like {@link tc}, except it also
 * validates the resolved response, so a 200 carrying an `errorContainer` comes
 * back as `Err` rather than `Ok`.
 *
 * What the `Ok` carries depends on the response wrapper, because the two have
 * different success conditions:
 *
 * | Response            | `Ok` carries      | Additionally `Err` when |
 * | ------------------- | ----------------- | ----------------------- |
 * | `SingleResponse<T>` | the unwrapped `T` | `data` is null          |
 * | `ItemsResponse<T>`  | the whole response | —                      |
 *
 * A `SingleResponse` with no `data` leaves the caller nothing to use, so it
 * fails. An empty `ItemsResponse` is a legitimate empty collection, so it
 * succeeds — and it comes back whole rather than unwrapped to `items`, so a
 * caller that needs `pagination` can still reach it.
 *
 * @param callback Invokes the API client method. Called with no arguments.
 * @param context The resource and operation being attempted, used to describe
 * the failure in Sentry.
 * @returns A promise resolving to `Ok` (carrying what the table above
 * describes) or `Err` (with a {@link RestError}). Narrow on the `ok`
 * discriminant.
 * @throws `InvalidSessionError` and `TwoFactorAuthError` only. Both are
 * handled above the call site, so they must not be swallowed here.
 *
 * @example
 * const result = await tcRestData(
 *   () => twApiClient.customerService.update(customer),
 *   { resource: "Customer", operation: "update" }
 * )
 * if (!result.ok) {
 *   result.report()
 *   throw new AbortSubmissionError()
 * }
 *
 * @example
 * const result = await tcRestData(
 *   () => twApiClient.accountDocumentService.index(params),
 *   { resource: "Document", operation: "read" }
 * )
 * if (!result.ok) {
 *   throw result.error
 * }
 * use(result.value.items)
 */
export async function tcRestData<T>(
  callback: () => Promise<ItemsResponse<T>>,
  context: RestContext
): Promise<Ok<ItemsResponse<T>> | Err<RestError>>
export async function tcRestData<T>(
  callback: () => Promise<SingleResponse<T>>,
  context: RestContext
): Promise<Ok<T> | Err<RestError>>
export async function tcRestData<T>(
  callback: () => Promise<SingleResponse<T> | ItemsResponse<T>>,
  context: RestContext
): Promise<Ok<T> | Ok<ItemsResponse<T>> | Err<RestError>> {
  const restResult = await tc(callback)

  // Handle API client throws
  if (!restResult.ok) {
    const e = (restResult as Err<RestError>).error
    rethrowIfHandledGlobally(e)

    const restError = RestError.fromThrown(e, context)
    return new Err(restError)
  }

  // The API client resolved successfully, but we still need to validate a 200-level response below

  // SingleResponse must have `data` to be considered successful
  if (restResult.value instanceof SingleResponse) {
    const restResponse = restResult.value
    if (restResponse.isError || !restResponse.hasData()) {
      const restError = RestError.fromResponse(restResponse, context)
      return new Err(restError)
    }
    return new Ok(restResponse.data)
  }

  // ItemsResponse will always have `items` (an empty array is still valid), so can only check if `isError`
  const restResponse = restResult.value
  if (restResponse.isError) {
    const restError = RestError.fromResponse(restResponse, context)
    return new Err(restError)
  }
  return new Ok(restResponse)
}
